import React, { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import './Patterns.css';

export type ButtonTone = 'primary' | 'subtle' | 'danger' | 'outline';

export interface SaveBarAction {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  tone?: ButtonTone;
}

/**
 * Typed status of a form's save lifecycle. `null` (or omitted) means idle —
 * status area renders empty. The kind drives the dot colour, the rendered
 * string, and (for `saving`) `aria-busy` on the bar.
 */
export type SaveBarStatus =
  | { kind: 'dirty' }
  | { kind: 'saving' }
  | { kind: 'error'; message?: string }
  | null;

export interface SaveBarProps {
  /**
   * Typed status. While migrations are in flight, a legacy ReactNode is
   * also accepted and rendered verbatim — to be removed once all forms
   * pass typed values.
   */
  status?: SaveBarStatus | ReactNode;
  primaryAction?: Omit<SaveBarAction, 'tone'>;
  secondaryActions?: SaveBarAction[];
}

function buttonClass(tone: ButtonTone | undefined): string {
  return `pat-btn pat-btn--${tone ?? 'subtle'}`;
}

function isTypedStatus(value: unknown): value is Exclude<SaveBarStatus, null> {
  return (
    typeof value === 'object' &&
    value !== null &&
    'kind' in (value as Record<string, unknown>) &&
    typeof (value as { kind: unknown }).kind === 'string' &&
    ['dirty', 'saving', 'error'].includes((value as { kind: string }).kind)
  );
}

const SaveBar: React.FC<SaveBarProps> = ({ status, primaryAction, secondaryActions }) => {
  const { t } = useTranslation();

  let dotClass: string | null = null;
  let statusText: ReactNode = null;
  const isSaving = isTypedStatus(status) && status.kind === 'saving';

  if (isTypedStatus(status)) {
    if (status.kind === 'dirty') {
      dotClass = 'pat-save-bar__dot--dirty';
      statusText = t('patterns.saveBar.status.unsaved');
    } else if (status.kind === 'saving') {
      dotClass = 'pat-save-bar__dot--saving';
      statusText = t('patterns.saveBar.status.saving');
    } else if (status.kind === 'error') {
      dotClass = 'pat-save-bar__dot--error';
      statusText = status.message ?? t('patterns.saveBar.status.errorGeneric');
    }
  } else if (status !== null && status !== undefined) {
    // Legacy ReactNode — render verbatim, no dot.
    statusText = status;
  }

  return (
    <div className="pat-save-bar" aria-busy={isSaving || undefined}>
      <div className="pat-save-bar__status" role="status" aria-live="polite">
        {dotClass && (
          <span
            data-testid="save-bar-status-dot"
            className={`pat-save-bar__dot ${dotClass}`}
            aria-hidden="true"
          />
        )}
        {statusText}
      </div>
      <div className="pat-save-bar__actions">
        {secondaryActions?.map((action) => (
          <button
            key={action.label}
            type="button"
            className={buttonClass(action.tone)}
            onClick={action.onClick}
            disabled={action.disabled}
          >
            {action.label}
          </button>
        ))}
        {primaryAction && (
          <button
            type="button"
            className={buttonClass('primary')}
            onClick={primaryAction.onClick}
            disabled={primaryAction.disabled}
            aria-disabled={(isSaving || primaryAction.disabled) || undefined}
          >
            {primaryAction.label}
          </button>
        )}
      </div>
    </div>
  );
};

export default SaveBar;
